<template>
  <!-- Page Header -->
  <PageHeader
    title="Mes commandes"
    subtitle="Gérez et suivez toutes vos commandes clients"
    class="mb-4 pr-10"
  />
  <div class="pl-3 pr-0 md:px-0">
    <!-- Filtres -->
    <OrderFilters :initial-filters="initialFilters" @filters="handleFilter" />
    <div class="w-full">
      <!-- Loading state -->
      <div v-if="isLoading" class="text-center py-8">
        <i class="fas fa-spinner fa-spin text-2xl text-black"></i>
        <p class="mt-2 text-black">Chargement des commandes...</p>
      </div>

      <!-- Content when not loading -->
      <div v-else>
        <!-- Pagination au-dessus du tableau -->
        <div class="my-6">
          <UnifiedPagination
            :current-page="currentPage"
            :total-items="totalItems"
            :items-per-page="itemsPerPage"
            :items-per-page-options="[10, 25, 50, 100]"
            :show-first-last-buttons="true"
            @update:current-page="handlePageChange"
            @update:items-per-page="handleItemsPerPageChange"
          />
        </div>

        <!-- Vue Desktop : Tableau -->
        <div
          class="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <ReusableTable
            :data="orders"
            :columns="tableHeaders"
            :show-pagination="false"
            :page-size="999999"
            @row-click="handleVoirDetail"
          >
            <!-- Slot pour l'ID -->
            <template #column-orderId="{ row }">
              <div class="text-center">
                <div class="font-bold text-black">
                  #{{ row.orderGroupId }}
                </div>
              </div>
            </template>

            <!-- Slot pour la date de commande -->
            <template #column-orderDate="{ row }">
              <div class="text-center">
                <div class="text-sm text-black">{{ row.orderDate }}</div>
              </div>
            </template>

            <!-- Slot pour la date de groupage -->
            <template #column-groupageDate="{ row }">
              <div class="text-center">
                <div class="text-sm text-black">
                  {{ row.groupageDate || "-" }}
                </div>
              </div>
            </template>

            <!-- Slot pour le transporteur -->
            <template #column-carrier="{ row }">
              <div
                :title="row.carrier as string || 'Aucun transporteur'"
                class="text-center"
              >
                <span class="font-medium text-black">{{
                  truncateText(row.carrier as string, 30)
                }}</span>
                <br v-if="row.isGroupage" />
                <strong v-if="row.isGroupage" class="text-black"
                  >(groupage)</strong
                >
              </div>
            </template>

            <!-- Slot pour le client -->
            <template #column-buyer="{ row }">
              <div class="text-center">
                <div
                  :title="row.buyer as string || 'Aucun acheteur'"
                  class="whitespace-normal break-words leading-tight"
                >
                  {{ (row.buyer as string) || "Aucun acheteur" }}
                </div>
                <div v-if="row.isNouveauAcheteur" class="mt-1">
                  <span class="bg-blue-600 px-2 py-1 rounded text-white text-xs"
                    >Nouveau Acheteur</span
                  >
                </div>
                <div v-if="row.isPremiereCommande" class="mt-1">
                  <span
                    class="inline-flex items-center gap-1 bg-gradient-to-r from-gray-900 to-black px-2.5 py-1 rounded-full text-white text-xs font-semibold shadow-md ring-1 ring-white/10"
                  >
                    <i class="fas fa-medal text-amber-400 text-[10px]"></i>
                    1ère commande
                  </span>
                </div>
              </div>
            </template>

            <!-- Slot pour le total HT -->
            <template #column-totalExclTax="{ row }">
              <div class="text-center">
                <span class="font-bold text-green-600"
                  >{{ row.totalExclTax }}€</span
                >
              </div>
            </template>

            <!-- Slot pour la rupture HT -->
            <template #column-shortageExclTax="{ row }">
              <div class="text-center">
                <span class="text-red-600 font-semibold">{{
                  (row.shortageExclTax as number) > 0
                    ? (row.shortageExclTax as number) + "€"
                    : "-"
                }}</span>
              </div>
            </template>

            <!-- Slot pour l'action 1 -->
            <template #column-status="{ row }">
              <div :class="[currentStatus( row.status as string ).style]">
                {{ currentStatus(row.status as string).label }}
              </div>
            </template>

            <!-- Slot pour l'action 2 -->
            <template #column-action="{ row }">
              <div class="flex gap-1" @click.stop>
                <!-- Boutons d'action -->
                <a
                  :href="getOrderDetailHref(row)"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                  class="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded text-white flex items-center justify-center transition-colors"
                  title="Voir dans un nouvel onglet"
                >
                  <i class="fas fa-eye text-xs"></i>
                </a>

                <!-- <button
                  @click.stop="openConfirmModal(row.orderId, 'shipped')"
                  class="w-8 h-8 bg-yellow-500 hover:bg-yellow-600 rounded text-white flex items-center justify-center transition-colors"
                  title="Expédier"
                  v-if="['4', '9'].includes(row.status as string)"
                >
                  <i class="fas fa-paper-plane text-xs"></i>
                </button> -->
                <button
                  @click.stop="handleValidateOrder(row)"
                  class="w-8 h-8 bg-green-500 hover:bg-green-600 rounded text-white flex items-center justify-center transition-colors"
                  title="Valider la commande"
                  v-if="['1', '9'].includes(row.status as string)"
                >
                  <i class="fas fa-check text-xs"></i>
                </button>
                <button
                  @click.stop="openCancelModal(row.orderId)"
                  class="w-8 h-8 bg-red-500 hover:bg-red-600 rounded text-white flex items-center justify-center transition-colors"
                  title="Annuler"
                  v-if="String(row.status) === '1'"
                >
                  <i class="fas fa-times text-xs"></i>
                </button>
              </div>
            </template>
          </ReusableTable>
        </div>

        <!-- Vue Mobile : Cartes verticales compactes -->
        <div class="md:hidden space-y-3 mr-0">
          <div
            v-for="order in orders"
            :key="order.orderId"
            @click="handleVoirDetail(order.orderId as number)"
            class="bg-white rounded-lg shadow-sm border border-gray-200 p-3 cursor-pointer hover:shadow-md transition-shadow"
          >
            <!-- En-tête avec ID et Statut -->
            <div
              class="flex justify-between items-start mb-2 pb-2 border-b border-gray-200"
            >
              <div>
                <div class="font-bold text-base text-black">
                  #{{ order.orderGroupId }}
                </div>
                <div class="text-xs text-black mt-0.5">
                  <div>Cmd: {{ order.orderDate }}</div>
                  <div v-if="order.groupageDate">
                    Grp: {{ order.groupageDate }}
                  </div>
                </div>
              </div>
              <div :class="[currentStatus(order.status as string).style]">
                {{ currentStatus(order.status as string).label }}
              </div>
            </div>

            <!-- Informations compactes en grille -->
            <div class="grid grid-cols-2 gap-2 text-xs mb-2">
              <div>
                <span class="text-black">Client:</span>
                <div class="font-medium text-black mt-0.5">
                  {{ truncateText(order.buyer as string, 20) }}
                </div>
                <div
                  v-if="order.isNouveauAcheteur || order.isPremiereCommande"
                  class="flex gap-1 mt-1"
                >
                  <span
                    v-if="order.isNouveauAcheteur"
                    class="bg-blue-600 px-1.5 py-0.5 rounded text-white text-[10px]"
                    >Nouveau</span
                  >
                  <span
                    v-if="order.isPremiereCommande"
                    class="inline-flex items-center gap-1 bg-gradient-to-r from-gray-900 to-black px-2 py-0.5 rounded-full text-white text-[10px] font-semibold shadow ring-1 ring-white/10"
                  >
                    <i class="fas fa-medal text-amber-400 text-[8px]"></i>
                    1ère
                  </span>
                </div>
              </div>
              <div>
                <span class="text-black">Transporteur:</span>
                <div class="font-medium text-black mt-0.5">
                  {{ truncateText(order.carrier as string, 18) }}
                </div>
                <div
                  v-if="order.isGroupage"
                  class="text-[10px] text-black mt-0.5"
                >
                  (groupage)
                </div>
              </div>
              <div>
                <span class="text-black">Total HT:</span>
                <div class="font-bold text-green-600 mt-0.5">
                  {{ order.totalExclTax }}€
                </div>
              </div>
              <div>
                <span class="text-black">Rupture HT:</span>
                <div class="text-red-600 font-semibold mt-0.5">
                  {{
                    (order.shortageExclTax as number) > 0
                      ? (order.shortageExclTax as number) + "€"
                      : "-"
                  }}
                </div>
              </div>
            </div>

            <!-- Actions compactes -->
            <div class="flex gap-1.5 pt-2 border-t border-gray-200" @click.stop>
              <a
                :href="getOrderDetailHref(order.orderId as number)"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
                class="flex-1 px-2 py-1.5 bg-blue-500 hover:bg-blue-600 rounded text-white text-xs font-medium flex items-center justify-center gap-1 transition-colors"
              >
                <i class="fas fa-eye text-[10px]"></i>
                Détails
              </a>
              <!-- <button
                v-if="['4', '9'].includes(order.status as string)"
                @click.stop="openConfirmModal(order.orderId, 'shipped')"
                class="px-2 py-1.5 bg-yellow-500 hover:bg-yellow-600 rounded text-white flex items-center justify-center transition-colors"
                title="Expédier"
              >
                <i class="fas fa-paper-plane text-xs"></i>
              </button> -->
              <button
                v-if="['1', '9'].includes(order.status as string)"
                @click.stop="handleValidateOrder(order)"
                class="px-2 py-1.5 bg-green-500 hover:bg-green-600 rounded text-white flex items-center justify-center transition-colors"
                title="Valider"
              >
                <i class="fas fa-check text-xs"></i>
              </button>
              <button
                v-if="order.status === '1'"
                @click.stop="openCancelModal(order.orderId as number)"
                class="px-2 py-1.5 bg-red-500 hover:bg-red-600 rounded text-white flex items-center justify-center transition-colors"
                title="Annuler"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Fin du conteneur v-else -->

      <!-- Pagination -->
      <div class="my-6">
        <UnifiedPagination
          :current-page="currentPage"
          :total-items="totalItems"
          :items-per-page="itemsPerPage"
          :items-per-page-options="[10, 25, 50, 100]"
          :show-first-last-buttons="true"
          @update:current-page="handlePageChange"
          @update:items-per-page="handleItemsPerPageChange"
        />
      </div>
    </div>
  </div>

  <CancelOrderModal
    v-model:is-visible="showCancelModal"
    :loading="isCanceling"
    @confirm="confirmCancelOrder"
  />

  <!-- Modal d'ajout de commentaire -->
  <CommentModal
    :show="showCommentModal"
    :order-id="selectedOrderId"
    @submit="submitComment"
    @cancel="closeCommentModal"
  />

  <!-- Modal de génération d'étiquettes -->
  <LabelGenerationModal
    v-if="selectedOrderForValidation && orderDetailForValidation"
    :isVisible="showLabelGenerationModal"
    :orderId="selectedOrderForValidation"
    :orderStatus="orderDetailForValidation.statut || 0"
    :idLivraison="
      orderDetailForValidation.livraison?.id_livraison
        ? Number(orderDetailForValidation.livraison.id_livraison)
        : null
    "
    :carrierName="orderDetailForValidation.livraison?.libelle || undefined"
    :orderDetailData="orderDetailForValidation"
    @update:isVisible="showLabelGenerationModal = $event"
    @labels-generated="handleLabelsGenerated"
    @parcel-removed="refreshValidationOrder"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  PageHeader,
  OrderFilters,
  ReusableTable,
  UnifiedPagination,
} from "@/components";
import type { Order, OrderFilters as OrderFiltersType } from "@/types/order";
import { formatDateFR } from "@/utils/date";
import { truncateText } from "@/utils/string";
import {
  ordersList,
  updateOrderStatus,
  addComment,
  getOrderById,
} from "@/api/orders";
import CommentModal from "@/components/common/CommentModal.vue";
import CancelOrderModal from "@/components/modals/CancelOrderModal.vue";
import LabelGenerationModal from "@/components/modals/LabelGenerationModal.vue";
import type { OrderDetailResponse } from "@/types";
import { useNotification } from "@/composables/useNotification";
import { useAuthStore } from "@/stores/auth";
import { useOrdersStore } from "@/stores/ordersStore";
import { invalidateDashboardCache } from "@/utils/dashboardCache";

const authStore = useAuthStore();
const ordersStore = useOrdersStore();

const router = useRouter();
const route = useRoute();
const { success, error: showError } = useNotification();

// État global de la page
const sellerOrders = ref<Order[]>([]);
const isLoading = ref(false);
// Filtres actifs (gestion d'état avec ref uniquement, pas d'URL)
const activeFilters = ref<OrderFiltersType | null>(null);

// États pour la modal d'annulation
const showCancelModal = ref(false);
const cancelOrderId = ref<number | null>(null);
const isCanceling = ref(false);

// États pour la modal de génération d'étiquettes
const showLabelGenerationModal = ref(false);
const selectedOrderForValidation = ref<number | null>(null);
const orderDetailForValidation = ref<OrderDetailResponse | null>(null);
const pendingGroupageStatus = ref<number | null>(null);

// États pour le modal de commentaire
const showCommentModal = ref(false);
const selectedOrderId = ref<number | null>(null);

const openCancelModal = (orderId: number) => {
  cancelOrderId.value = orderId;
  showCancelModal.value = true;
};

const confirmCancelOrder = async (
  reasonCode: string,
  reasonDetail?: string,
) => {
  if (!cancelOrderId.value || isCanceling.value) return;

  isCanceling.value = true;
  try {
    const isCancelled = await cancelOrder(
      cancelOrderId.value,
      reasonCode,
      reasonDetail,
    );
    if (isCancelled) {
      showCancelModal.value = false;
      cancelOrderId.value = null;
    }
  } finally {
    isCanceling.value = false;
  }
};

// Gérer la validation d'une commande
const handleValidateOrder = (
  row: Order & { orderId?: number; orderGroupId?: number },
) => {
  const orderIdRaw = (row as any).orderId || row.id;
  if (!orderIdRaw) {
    showError("ID de commande invalide");
    return;
  }

  const orderId = Number(orderIdRaw);
  if (isNaN(orderId)) {
    showError("ID de commande invalide");
    return;
  }

  // Naviguer vers la page de détail et scroller vers le statut
  router.push({
    name: "orderdetails-detail",
    params: { id: orderId.toString() },
    query: { ...route.query, scrollTo: "statut" },
  });
};

// Gérer la génération réussie des étiquettes
const handleLabelsGenerated = async () => {
  if (!selectedOrderForValidation.value || pendingGroupageStatus.value === null)
    return;

  try {
    // Confirmer la commande avec le statut approprié
    await changeStatus(
      selectedOrderForValidation.value,
      pendingGroupageStatus.value,
    );

    // Invalider le cache du dashboard pour mettre à jour les statistiques
    const currentUser = authStore.user;
    if (currentUser?.id) {
      invalidateDashboardCache(currentUser.id);
    }

    // Recharger les données (les filtres sont déjà dans activeFilters)
    await loadOrders();

    // Message de succès
    success("Commande confirmée et étiquettes générées avec succès");

    // Fermer la modal et réinitialiser
    showLabelGenerationModal.value = false;
    selectedOrderForValidation.value = null;
    orderDetailForValidation.value = null;
    pendingGroupageStatus.value = null;
  } catch (error) {
    showError("Erreur lors de la confirmation de la commande");
  }
};

const refreshValidationOrder = async () => {
  if (!selectedOrderForValidation.value) return;
  orderDetailForValidation.value = await getOrderById(
    selectedOrderForValidation.value,
  );
};

// Méthodes pour le modal de commentaire
const handleComment = (orderId: number) => {
  selectedOrderId.value = orderId;
  showCommentModal.value = true;
};

const closeCommentModal = () => {
  showCommentModal.value = false;
  selectedOrderId.value = null;
};

const submitComment = async (commentData: { comment: string }) => {
  if (selectedOrderId.value && commentData.comment.trim()) {
    try {
      await addComment(selectedOrderId.value, commentData.comment);
      // Recharger les données (les filtres sont déjà dans activeFilters)
      await loadOrders();
      closeCommentModal();
    } catch (error) {
      // Gérer l'erreur (afficher un message à l'utilisateur)
    }
  }
};
// ----------------------------------------

// État de la pagination
const currentPage = ref(1);
const itemsPerPage = ref(25);
const totalItems = ref(0);

const parsePositiveInt = (
  value: unknown,
  fallback: number,
  allowedValues?: number[],
): number => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return fallback;
  if (allowedValues && !allowedValues.includes(parsed)) return fallback;
  return parsed;
};

const syncPaginationFromQuery = () => {
  currentPage.value = parsePositiveInt(route.query.page, 1);
  itemsPerPage.value = parsePositiveInt(route.query.limit, 25, [
    10, 25, 50, 100,
  ]);
};

const syncQueryFromPagination = async () => {
  const nextQuery = {
    ...route.query,
    page: String(currentPage.value),
    limit: String(itemsPerPage.value),
  };
  await router.replace({ query: nextQuery });
};

const orders = computed(() => {
  return sellerOrders.value.map((order: any) => {
    order.orderId = Number(order.id_commande); // Convertir en nombre
    // Pour les groupages (UPS groupage, etc.) : si id_commande_groupe manquant, utiliser id_commande
    const groupageValue = order.commandeGroupe?.groupage;
    const isGroupage = groupageValue == 1 || groupageValue == "1";
    const rawGroupId = Number(order.id_commande_groupe);
    order.orderGroupId =
      rawGroupId && !isNaN(rawGroupId) ? rawGroupId : order.orderId;
    order.packageCount = order.nb_colis;
    // Construire le texte du transporteur
    const carrierLibelle = order.livraison?.libelle || "Non défini";
    order.carrier = carrierLibelle;
    order.isGroupage = !!isGroupage;
    order.buyer = order.acheteur?.nomSociete || "Non défini";
    order.isPremiereCommande = !!order.isPremiereCommande;
    // Total HT = montantCA si disponible et non nul/zéro, sinon montantApresRemise
    order.totalExclTax =
      order.montantCA != null && order.montantCA !== 0
        ? order.montantCA
        : order.montantApresRemise ?? order.montantTotal;
    // Rupture HT = montant des produits en rupture après remise (comme l'ancien système)
    order.shortageExclTax = order.montantRuptureHT ?? 0;
    order.status = order.commandeStatut?.id_statut || order.statut;
    order.orderDate = formatDateFR(order.dateCommande);
    order.groupageDate =
      isGroupage && order.commandeGroupe?.date_expedition
        ? formatDateFR(order.commandeGroupe.date_expedition)
        : null;
    order.shipmentDate = formatDateFR(order.dateExpedition);
    return order;
  });
});

// En-têtes du tableau
const tableHeaders = ref([
  { key: "orderId", title: "N° commande", sortable: false, align: "center" },
  {
    key: "orderDate",
    title: "Date commande",
    sortable: false,
    align: "center",
  },
  {
    key: "groupageDate",
    title: "Date groupage",
    sortable: false,
    align: "center",
  },
  { key: "buyer", title: "Client", sortable: false, align: "center" },
  { key: "totalExclTax", title: "Total HT", sortable: false, align: "center" },
  {
    key: "shortageExclTax",
    title: "Rupture HT",
    sortable: false,
    align: "center",
  },
  { key: "carrier", title: "Transporteur", sortable: false, align: "center" },
  { key: "status", title: "Statut", sortable: false, align: "center" },
  { key: "action", title: "Actions", sortable: false, align: "center" },
]);

const statusLegend: Record<
  string,
  { color: string; border: string; label: string; text?: string }
> = {
  "0": {
    color: "bg-gray-300",
    border: "!border-l-gray-300",
    label: "EN PANIER",
  },
  "1": {
    color: "bg-yellow-300",
    border: "!border-l-yellow-300",
    label: "NOUVEAU",
  },
  "2": {
    color: "bg-green-300",
    border: "!border-l-green-300",
    label: "EXPÉDIÉE",
  },
  "3": { color: "bg-red-300", border: "!border-l-red-300", label: "ANNULÉE" },
  "4": {
    color: "bg-blue-300",
    border: "!border-l-blue-300",
    label: "CONFIRMÉE",
  },
  "6": {
    color: "bg-pink-300",
    border: "!border-l-pink-300",
    label: "PRÉPARATION DE GROUPAGE",
  },
  "7": { color: "bg-cyan-300", border: "!border-l-cyan-300", label: "PICKUP" },
  "8": {
    color: "bg-lime-300",
    border: "!border-l-lime-300",
    label: "PICKUP EFFECTUÉ",
  },
  "9": {
    color: "bg-amber-300",
    border: "!border-l-amber-300",
    label: "NOUVEAU",
  },
  "10": {
    color: "bg-gray-400",
    border: "!border-l-gray-400",
    label: "SAUVEGARDÉE",
  },
  "11": {
    color: "bg-indigo-300",
    border: "!border-l-indigo-300",
    label: "VALIDATION PAIEMENT",
  },
  "12": {
    color: "bg-orange-300",
    border: "!border-l-orange-300",
    label: "BLOQUÉE",
  },
  "14": {
    color: "bg-[#DDA0DD]",
    border: "!border-l-[#DDA0DD]",
    text: "text-purple-900",
    label: "REÇU AU DÉPÔT",
  },
};

// Méthodes de gestion des données
const currentStatus = (statusId: string) => {
  const entry = statusLegend[statusId];
  if (!entry) {
    return {
      style:
        "inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-300 text-black",
      label: "—",
    };
  }
  const bg = entry.color;
  const txt =
    entry.text || bg.replace(/^bg-/, "text-").replace(/-(100|200|300|400)/, "-800");
  return {
    style: `inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-semibold ${bg} ${txt}`,
    label: entry.label,
  };
};

const loadOrders = async () => {
  isLoading.value = true;
  try {
    const page = currentPage.value;
    const limit = itemsPerPage.value;
    // Utiliser les filtres actifs stockés dans activeFilters
    const orders: {
      data: Order[];
      total: number;
      page: number;
      limit: number;
    } = await ordersList(page, limit, activeFilters.value || undefined);

    totalItems.value = orders.total;
    sellerOrders.value = orders.data;
  } finally {
    isLoading.value = false;
  }
};

const cancelOrder = async (
  orderId: number,
  reasonCode: string,
  reasonDetail?: string,
): Promise<boolean> => {
  try {
    // La mise en rupture des produits est maintenant gérée automatiquement
    // par le backend lors de l'annulation (status=3)
    await changeStatus(orderId, 3, reasonCode, reasonDetail);

    // Invalider le cache du dashboard pour mettre à jour les statistiques
    const currentUser = authStore.user;
    if (currentUser?.id) {
      invalidateDashboardCache(currentUser.id);
    }

    // Recharger les données (les filtres sont déjà dans activeFilters)
    await loadOrders();

    success("Commande annulée avec succès");
    return true;
  } catch (err) {
    showError("Erreur lors de la modification du statut de la commande");
    return false;
  }
};

const changeStatus = async (
  orderId: number,
  newStatus: number,
  reasonCode?: string,
  reasonDetail?: string,
) => {
  const orderDetail = await updateOrderStatus(
    orderId,
    newStatus,
    reasonCode,
    reasonDetail,
  );
  return orderDetail as any; // Conversion simplifiée pour compatibilité
};

const handleFilter = async (filters: OrderFiltersType) => {
  currentPage.value = 1;

  // Vérifier si tous les filtres sont vides
  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== null && value !== undefined && value !== "",
  );

  // Stocker les filtres actifs (gestion d'état)
  activeFilters.value = hasActiveFilters ? { ...filters } : null;

  // Sauvegarder les filtres dans le store Pinia (null si tous vides)
  ordersStore.setFilters(hasActiveFilters ? filters : null);

  await syncQueryFromPagination();
  await loadOrders();
};

// Méthodes de pagination
const handlePageChange = async (newPage: number) => {
  currentPage.value = newPage;
  await syncQueryFromPagination();
  await loadOrders();
};

const handleItemsPerPageChange = async (newItemsPerPage: number) => {
  itemsPerPage.value = newItemsPerPage;
  currentPage.value = 1; // Retour à la première page lors du changement
  await syncQueryFromPagination();
  await loadOrders();
};

function resolveOrderDetailId(orderIdOrRow: number | Order): number | null {
  const orderId =
    typeof orderIdOrRow === "number"
      ? orderIdOrRow
      : (orderIdOrRow.orderId as number) ||
        (orderIdOrRow as any).id_commande ||
        (orderIdOrRow as any).id;

  if (!orderId) {
    return null;
  }
  return orderId;
}

function getOrderDetailHref(orderIdOrRow: number | Order): string {
  const orderId = resolveOrderDetailId(orderIdOrRow);
  if (orderId === null) {
    return "#";
  }

  return router.resolve({
    name: "orderdetails-detail",
    params: { id: orderId.toString() },
    query: route.query,
  }).href;
}

const handleVoirDetail = (orderIdOrRow: number | Order) => {
  const orderId = resolveOrderDetailId(orderIdOrRow);
  if (orderId === null) {
    return;
  }

  router.push({
    name: "orderdetails-detail",
    params: { id: orderId.toString() },
    query: route.query,
  });
};

// Fonction pour obtenir la classe CSS selon le status
const getRowClass = (row: any) => {
  const result = `border-l-12 ${
    statusLegend[row.status]?.border || "!border-l-gray-300"
  }`;
  return result;
};

// Filtres initiaux pour le composant OrderFilters
const initialFilters = computed<OrderFiltersType>(() => {
  return ordersStore.filters ?? {};
});

// Initialisation
onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: "auth" });
    return;
  }

  // Restaurer les filtres depuis le store (mis par le dashboard ou la navigation interne)
  if (ordersStore.filters) {
    activeFilters.value = { ...ordersStore.filters };
  }

  syncPaginationFromQuery();
  await syncQueryFromPagination();

  // Charger les commandes
  await loadOrders();
});
</script>

<style scoped></style>
